// Accès Firebase : authentification anonyme, App Check, fonctions du Hall of Fame et lecture de sa synthèse.
// La config vient des variables VITE_FIREBASE_* (voir .env.example). Sans config : mode local.
// Le SDK (l'essentiel du poids du bundle) est chargé à la demande dans init(), dans un chunk à part : le jeu s'affiche sans
// l'attendre, et il n'est jamais téléchargé en mode local.
// Le jeu n'écrit jamais dans Firestore : startRun donne un ticket au début d'une partie, submitScore inscrit le score d'une
// partie gagnée (functions/index.js). Le Hall of Fame se lit en un seul document, la synthèse (un getDoc, jamais d'écoute).
// App Check (reCAPTCHA Enterprise) prouve aux fonctions que l'appel vient du jeu ; en développement, un jeton de debug
// (VITE_APPCHECK_DEBUG_TOKEN, ou celui que le SDK affiche dans la console) ; avec les émulateurs, pas d'App Check du tout.
import { sanitizeSummary } from '../core/leaderboard.js';

const FUNCTIONS_REGION = 'europe-west1';

function loadFirebaseSdk(withAppCheck) {
  return Promise.all([
    import('firebase/app'), import('firebase/auth'), import('firebase/firestore'), import('firebase/functions'),
    withAppCheck ? import('firebase/app-check') : {},
  ]).then((modules) => Object.assign({}, ...modules));
}

const env = import.meta.env;

function readConfig() {
  const cfg = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  };
  const useEmulators = env.VITE_USE_EMULATORS === 'true';
  // Avec les émulateurs, un projet "demo-*" suffit : aucune clé réelle n'est nécessaire
  if (useEmulators) {
    cfg.projectId = cfg.projectId || 'demo-zero-maze';
    cfg.apiKey = cfg.apiKey || 'demo-api-key';
  }
  if (!cfg.projectId || !cfg.apiKey) return null;
  return cfg;
}

/** Motif d'un refus de submitScore (functions/src/submission.js), ou le code de l'erreur. */
const reasonOf = (e) => e?.details?.reason ?? e?.code ?? 'error';

export function createFirebaseService() {
  let sdk = null; // modules Firebase, une fois chargés (voir loadFirebaseSdk)
  let db = null;
  let functions = null;
  let active = false;
  const appId = env.VITE_APP_ID || 'zero-maze';

  /** Initialise Firebase et connecte l'utilisateur anonymement. Retourne true si actif. */
  async function init() {
    const config = readConfig();
    if (!config) {
      console.info('[firebase] Mode local : pas de configuration Firebase.');
      return false;
    }
    const useEmulators = env.VITE_USE_EMULATORS === 'true';
    try {
      sdk = await loadFirebaseSdk(!useEmulators);
      const app = sdk.initializeApp(config);
      if (!useEmulators) initAppCheck(app);
      db = sdk.getFirestore(app);
      const auth = sdk.getAuth(app);
      functions = sdk.getFunctions(app, FUNCTIONS_REGION);

      if (useEmulators) {
        // Ports de firebase.json, sauf si ceux de l'hôte diffèrent (VITE_EMULATOR_*_PORT, voir .env.example)
        const host = env.VITE_EMULATOR_HOST || 'localhost';
        const port = (name, fallback) => Number(env[`VITE_EMULATOR_${name}_PORT`]) || fallback;
        sdk.connectAuthEmulator(auth, `http://${host}:${port('AUTH', 9099)}`, { disableWarnings: true });
        sdk.connectFirestoreEmulator(db, host, port('FIRESTORE', 8080));
        sdk.connectFunctionsEmulator(functions, host, port('FUNCTIONS', 5001));
      }

      await sdk.signInAnonymously(auth);
      active = Boolean(auth.currentUser);
      return active;
    } catch (e) {
      console.error("[firebase] Erreur d'initialisation :", e);
      active = false;
      return false;
    }
  }

  function initAppCheck(app) {
    const siteKey = env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;
    if (!siteKey) {
      console.warn('[firebase] Pas de clé reCAPTCHA Enterprise : les fonctions du Hall of Fame refuseront les scores.');
      return;
    }
    // En développement, le jeton de debug remplace reCAPTCHA (à enregistrer dans la console Firebase, App Check)
    if (env.DEV) self.FIREBASE_APPCHECK_DEBUG_TOKEN = env.VITE_APPCHECK_DEBUG_TOKEN || true;
    sdk.initializeAppCheck(app, { provider: new sdk.ReCaptchaEnterpriseProvider(siteKey), isTokenAutoRefreshEnabled: true });
  }

  /** Ticket d'une partie qui commence (functions/index.js#startRun), ou null si l'appel échoue. */
  async function startRun() {
    if (!active) return null;
    try {
      const { data } = await sdk.httpsCallable(functions, 'startRun')();
      return typeof data?.ticket === 'string' ? data.ticket : null;
    } catch (e) {
      console.error('[firebase] startRun :', e);
      return null;
    }
  }

  /**
   * Inscrit le score d'une partie gagnée. Le jeton App Check est à usage unique (consommé par la fonction) : un appel
   * rejoué est refusé.
   * @param {{ticket: string, score: number, profile: {pseudo: string, avatar: string, city?: string, countryCode?: string}}} sent
   * @returns {Promise<{ok: true, id: string, top: object}|{ok: false, reason: string}>}
   */
  async function submitScore(sent) {
    if (!active) return { ok: false, reason: 'offline' };
    try {
      const call = sdk.httpsCallable(functions, 'submitScore', { limitedUseAppCheckTokens: true });
      const { data } = await call(sent);
      return { ok: true, id: String(data.id), top: sanitizeSummary(data.top) };
    } catch (e) {
      console.error('[firebase] submitScore :', e);
      return { ok: false, reason: reasonOf(e) };
    }
  }

  /** La synthèse du Hall of Fame { updated, all, month, week }, ou null si elle est illisible. */
  async function fetchTop() {
    if (!active) return null;
    try {
      const snap = await sdk.getDoc(sdk.doc(db, 'artifacts', appId, 'public', 'data', 'leaderboards', 'top'));
      return sanitizeSummary(snap.exists() ? snap.data() : null);
    } catch (e) {
      console.error('[firebase] Lecture du Hall of Fame :', e);
      return null;
    }
  }

  return {
    init,
    startRun,
    submitScore,
    fetchTop,
    get isActive() { return active; },
  };
}
